import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { MobileSearchOverlay } from './MobileSearchOverlay';
import {
  Home,
  Search,
  ShoppingBag,
  MessageSquare,
  User,
  Plus,
  Bell,
  MoreVertical,
  Calendar,
  ClipboardList,
  PartyPopper,
  LogOut,
  TrendingUp,
  HelpCircle,
  LayoutGrid,
  Briefcase as EventManagerIcon,
  Heart,
  FileText,
  Image as ImageIcon,
  Handshake,
  Sliders,
  Star,
  Sun,
  Moon,
} from 'lucide-react';
import { auth, db } from '../../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query as firestoreQuery, where, onSnapshot as onSnapshotListener } from 'firebase/firestore';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useNotificationBell } from '../../hooks/useNotificationBell';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useScrollLock } from '../../hooks/useScrollLock';
import { useBackButton } from '../../hooks/useBackButton';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { preloadRoute } from '../../utils/routePrefetch';

const navItems = [
  { path: '/', icon: Home, label: 'Home' },
  { path: '/explore', icon: Search, label: 'Explore' },
  { path: '/services', icon: ShoppingBag, label: 'Services' },
  { path: '/profile', icon: User, label: 'Profile' },
];

// Premium plan (Apex only)
const premiumPlans = [
  { key: 'apex', label: 'Apex', icon: '💎' }
];

export const MobileNav: React.FC = () => {
  const location = useLocation();
  const { isAdmin, eventManagerProfile } = useAuth();
  const [showMobileApexMenu, setShowMobileApexMenu] = useState(false);
  const [isClosingMenu, setIsClosingMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [messagesUnreadCount, setMessagesUnreadCount] = useState<number>(0);
  const [currentUid, setCurrentUid] = useState<string | null>(null);
  const [currentPremiumPlan] = useState(premiumPlans[0]);
  const { unreadCount } = useNotifications();
  const { isAnimating } = useNotificationBell(unreadCount, false);
  const { resolvedTheme, toggleTheme } = useTheme();
  const dragControls = useDragControls();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  // Smooth dismiss handler for the apex menu
  const handleCloseMenu = useCallback(() => {
    if (isClosingMenu) return;
    setIsClosingMenu(true);
    setTimeout(() => {
      setShowMobileApexMenu(false);
      setIsClosingMenu(false);
    }, 220);
  }, [isClosingMenu]);

  // Reset closing state when opening
  useEffect(() => {
    if (showMobileApexMenu) {
      setIsClosingMenu(false);
    }
  }, [showMobileApexMenu]);

  // Auto-close mobile menu and search overlay on route change
  useEffect(() => {
    setShowMobileApexMenu(false);
    setIsClosingMenu(false);
    setShowSearch(false);
  }, [location.pathname, location.search]);

  // Lock background scrolling while apex menu is open
  useScrollLock({
    isLocked: showMobileApexMenu && !isClosingMenu,
    preserveScrollbarWidth: true,
  });

  // Hardware back button support for 3-dot apex menu and search overlay
  useBackButton(showMobileApexMenu && !isClosingMenu, handleCloseMenu);
  useBackButton(showSearch, () => setShowSearch(false));

  // Escape key handler to consistently dismiss mobile apex drawer, search, or dialogs
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isLogoutConfirmOpen) {
          setIsLogoutConfirmOpen(false);
        } else if (showSearch) {
          setShowSearch(false);
        } else if (showMobileApexMenu) {
          handleCloseMenu();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLogoutConfirmOpen, showSearch, showMobileApexMenu, handleCloseMenu]);

  // Compute unread messages across conversations
  useEffect(() => {
    if (!currentUid) {
      setMessagesUnreadCount(0);
      return () => { };
    }

    const convCol = collection(db, 'conversations');
    const qConv = firestoreQuery(
      convCol,
      where('participants', 'array-contains', currentUid)
    );

    const unsub = onSnapshotListener(qConv, {
      next: (querySnapshot) => {
        let count = 0;
        let lastSeen: Record<string, number> = {};

        try {
          const raw = localStorage.getItem('messages_last_seen');
          lastSeen = raw ? JSON.parse(raw) : {};
        } catch {
          lastSeen = {};
        }

        querySnapshot.forEach((doc) => {
          const data = doc.data() as {
            updatedAt?: { toMillis?: () => number; seconds?: number };
            lastSenderId?: string;
          };

          if (!data) return;

          let updatedMs = 0;
          const ts = data.updatedAt;

          if (ts) {
            if (typeof ts.toMillis === 'function') {
              updatedMs = ts.toMillis();
            } else if (typeof ts.seconds === 'number') {
              updatedMs = ts.seconds * 1000;
            }
          }

          const seen = lastSeen[doc.id] ?? 0;
          const lastSenderId = data.lastSenderId;

          if (updatedMs > seen && lastSenderId && lastSenderId !== currentUid) {
            count += 1;
          }
        });

        const onMessagesPage = location.pathname.startsWith('/messages');
        if (onMessagesPage) {
          setMessagesUnreadCount(0);
          return;
        }

        setMessagesUnreadCount(count);
      },
      error: () => setMessagesUnreadCount(0)
    });

    return () => unsub();
  }, [currentUid, location.pathname]);

  // Subscribe to auth
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      if (!u) {
        setCurrentUid(null);
        return;
      }
      setCurrentUid(u.uid);
    });
    return () => unsubAuth();
  }, []);

  const isAdminPage = location.pathname.startsWith('/admin');
  const isMessagesPage = location.pathname.startsWith('/messages');
  const shouldShowHeader = !isAdminPage && !isMessagesPage;
  const isInsideChat = location.pathname.startsWith('/messages/') && location.pathname !== '/messages';

  return (
    <>
      {/* Mobile Header */}
      {shouldShowHeader && (
        <header
          className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#080A0F]/95 backdrop-blur-md border-b border-neutral-200 dark:border-white/[0.08] shadow-sm"
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
          }}
        >
          <div className="flex items-center justify-between h-14 px-4">
            <Link to="/" className="flex items-center space-x-2 group">
              <motion.div
                className="relative w-10 h-10 rounded-xl overflow-hidden shadow-md shadow-purple-200/50 dark:shadow-purple-800/50"
                whileHover={{ scale: 1.05, rotate: 2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-[#8649ED] via-[#FF881D] to-[#8549ED] opacity-15 group-hover:opacity-25 transition-opacity duration-300"
                  animate={{
                    background: [
                      "linear-gradient(45deg, #8649ED, #FF881D, #8549ED)",
                      "linear-gradient(90deg, #8549ED, #8649ED, #FF881D)",
                      "linear-gradient(135deg, #FF881D, #8549ED, #8649ED)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                />
                <motion.img
                  src="/crevvi-logo.png"
                  alt="Crevvi Logo"
                  className="w-full h-full object-contain relative z-10 filter drop-shadow-sm"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                />
              </motion.div>
              <div className="text-xl font-black flex items-center tracking-tight">
                <span style={{ color: '#8649ED' }}>C</span>
                <span style={{ color: '#FF881D' }}>r</span>
                <span style={{ color: '#0BB475' }}>e</span>
                <span style={{ color: '#3B82F6' }}>v</span>
                <span style={{ color: '#3B82F6' }}>v</span>
                <span style={{ color: '#8649ED' }}>i</span>
              </div>
            </Link>

            <div className="flex items-center space-x-3">
              {/* Notifications (Mobile) */}
              <Link
                to="/notifications"
                onPointerEnter={() => preloadRoute('/notifications')}
                onTouchStart={() => preloadRoute('/notifications')}
                className="relative p-1 text-neutral-600 dark:text-dark-500"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Notifications"
                  className="relative"
                >
                  <div className="relative">
                    <motion.div
                      className={`p-2 rounded-full transition-colors duration-200 ${location.pathname.startsWith('/notifications')
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 shadow-sm'
                        : 'text-neutral-600 dark:text-dark-500 hover:text-neutral-900 dark:hover:text-dark-900'
                        }`}
                      animate={isAnimating ? {
                        scale: [1, 1.2, 1],
                        rotate: [0, 10, -10, 5, -5, 2, -2, 0],
                      } : {}}
                      transition={{
                        duration: 0.8,
                        ease: "easeInOut",
                        times: [0, 0.2, 0.4, 0.6, 0.8, 0.9, 1],
                      }}
                    >
                      <Bell
                        size={20}
                        className={`transition-all duration-200 ${location.pathname.startsWith('/notifications')
                          ? 'fill-purple-600/20 dark:fill-purple-400/20 stroke-[2.2] scale-105 text-purple-600 dark:text-purple-400'
                          : ''
                          } ${isAnimating ? 'animate-pulse' : ''}`}
                      />
                    </motion.div>
                    <AnimatePresence>
                      {isAnimating && (
                        <>
                          <motion.div
                            className="absolute inset-0 rounded-full border-2 border-purple-300"
                            initial={{ opacity: 0.7, scale: 1 }}
                            animate={{ opacity: 0, scale: 1.5 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1 }}
                          />
                          <motion.div
                            className="absolute inset-0 rounded-full bg-purple-100/30"
                            initial={{ opacity: 0.3, scale: 1 }}
                            animate={{ opacity: 0, scale: 1.8 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1.2 }}
                          />
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] leading-[16px] text-center font-semibold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </motion.div>
              </Link>

              {/* Messages shortcut */}
              <Link
                to="/messages"
                onPointerEnter={() => preloadRoute('/messages')}
                onTouchStart={() => preloadRoute('/messages')}
                className="relative p-1 text-neutral-600 dark:text-dark-500"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Messages"
                  className="relative"
                >
                  <div className={`p-2 rounded-full transition-colors duration-200 ${location.pathname.startsWith('/messages')
                    ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-neutral-600 dark:text-dark-500 hover:text-neutral-900 dark:hover:text-dark-900'
                    }`}>
                    <MessageSquare
                      size={20}
                      className={`transition-all duration-200 ${location.pathname.startsWith('/messages')
                        ? 'fill-purple-600/20 dark:fill-purple-400/20 stroke-[2.2] scale-105'
                        : ''
                        }`}
                    />
                  </div>
                  {messagesUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-pink-500 text-white text-[10px] leading-[16px] text-center font-semibold shadow-sm">
                      {messagesUnreadCount > 9 ? '9+' : messagesUnreadCount}
                    </span>
                  )}
                </motion.div>
              </Link>

              {/* Apex Menu (Three-dot button) */}
              <div className="relative">
                <motion.button
                  onClick={() => setShowMobileApexMenu(!showMobileApexMenu)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Menu"
                  className="relative p-2 text-neutral-600 dark:text-dark-500 hover:text-neutral-900 dark:hover:text-dark-900 transition-colors"
                >
                  <MoreVertical size={20} />
                </motion.button>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Mobile Apex Menu Portal (Rendered to body to prevent header stacking context trapping) */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {showMobileApexMenu && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: isClosingMenu ? 0 : 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998] touch-none"
                onClick={handleCloseMenu}
              />
              {/* Bottom Sheet Menu */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: isClosingMenu ? '100%' : 0 }}
                exit={{ y: '100%' }}
                transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
                drag="y"
                dragListener={false}
                dragControls={dragControls}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.7 }}
                onDragEnd={(_, info) => {
                  if (info.offset.y > 60 || info.velocity.y > 200) {
                    handleCloseMenu();
                  }
                }}
                className="fixed bottom-0 inset-x-0 z-[99999] max-h-[85vh] flex flex-col bg-white dark:bg-dark-50 rounded-t-3xl shadow-2xl border-t border-neutral-200 dark:border-dark-200 overflow-hidden touch-pan-y"
              >
                {/* Drag Handle Bar */}
                <div
                  className="flex justify-center pt-3 pb-3 bg-white dark:bg-dark-50 shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
                  onPointerDown={(e) => dragControls.start(e)}
                  onClick={handleCloseMenu}
                >
                  <div className="w-12 h-1.5 bg-neutral-300 dark:bg-dark-300 rounded-full hover:bg-neutral-400 dark:hover:bg-dark-400 transition-colors" />
                </div>

                {/* Scrollable Menu Items Container */}
                <div className="flex-1 overflow-y-auto px-4 pb-[calc(3rem+env(safe-area-inset-bottom,0px))] pt-1 space-y-1 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {/* Premium Upgrade */}
                  <Link
                    to={`/?premium=1&plan=${currentPremiumPlan.key}`}
                    onClick={handleCloseMenu}
                    className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-neutral-100 dark:hover:bg-dark-100 transition-colors rounded-lg active:bg-neutral-200 dark:active:bg-dark-200"
                  >
                    <span className="text-xl">{currentPremiumPlan.icon}</span>
                    <div className="flex flex-col flex-1">
                      <span className="font-medium text-sm">Upgrade to {currentPremiumPlan.label}</span>
                      <span className="text-[11px] text-neutral-600 dark:text-dark-600">Get premium features</span>
                    </div>
                  </Link>

                  {/* Messages */}
                  <Link
                    to="/messages"
                    onPointerEnter={() => preloadRoute('/messages')}
                    onTouchStart={() => preloadRoute('/messages')}
                    onClick={handleCloseMenu}
                    className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-neutral-100 dark:hover:bg-dark-100 transition-colors rounded-lg active:bg-neutral-200 dark:active:bg-dark-200"
                  >
                    <MessageSquare size={18} className="text-pink-500" />
                    <span className="font-medium text-sm">Messages</span>
                  </Link>

                  {/* Admin Panel (Conditional) */}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onPointerEnter={() => preloadRoute('/admin')}
                      onTouchStart={() => preloadRoute('/admin')}
                      onClick={handleCloseMenu}
                      className="flex items-center gap-3 px-4 py-3 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors rounded-lg active:bg-blue-100 dark:active:bg-blue-900/20 font-bold"
                    >
                      <LayoutGrid size={18} />
                      <span className="font-medium text-sm">Admin Panel</span>
                    </Link>
                  )}

                  {/* Requirements */}
                  <Link
                    to="/requirements"
                    onPointerEnter={() => preloadRoute('/requirements')}
                    onTouchStart={() => preloadRoute('/requirements')}
                    onClick={handleCloseMenu}
                    className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-neutral-100 dark:hover:bg-dark-100 transition-colors rounded-lg active:bg-neutral-200 dark:active:bg-dark-200"
                  >
                    <ClipboardList size={18} className="text-emerald-500" />
                    <span className="font-medium text-sm">Requirements</span>
                  </Link>

                  {/* Event Discovery Section */}
                  <div className="pt-2 pb-2 border-y border-neutral-100 dark:border-dark-200 my-2 space-y-1">
                    <p className="px-4 text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-widest mb-2">Event Discovery</p>

                    {/* Event Requirements */}
                    <Link
                      to="/event-matches-page?view=eventMatches"
                      onPointerEnter={() => preloadRoute('/event-matches-page')}
                      onTouchStart={() => preloadRoute('/event-matches-page')}
                      onClick={handleCloseMenu}
                      className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors rounded-lg"
                    >
                      <Calendar size={18} className="text-indigo-500" />
                      <span className="font-medium text-sm">Event Requirements</span>
                    </Link>

                    {/* AI Matches */}
                    <Link
                      to="/event-matches-page?view=matches"
                      onPointerEnter={() => preloadRoute('/event-matches-page')}
                      onTouchStart={() => preloadRoute('/event-matches-page')}
                      onClick={handleCloseMenu}
                      className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors rounded-lg"
                    >
                      <Handshake size={18} className="text-pink-500" />
                      <span className="font-medium text-sm">AI Recommendations</span>
                    </Link>

                    {/* Events List */}
                    <Link
                      to="/event-matches-page?view=events"
                      onPointerEnter={() => preloadRoute('/event-matches-page')}
                      onTouchStart={() => preloadRoute('/event-matches-page')}
                      onClick={handleCloseMenu}
                      className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors rounded-lg"
                    >
                      <PartyPopper size={18} className="text-amber-500" />
                      <span className="font-medium text-sm">Events Discovery</span>
                    </Link>

                    {/* Your Events */}
                    <Link
                      to="/event-matches-page?view=yourEvents"
                      onPointerEnter={() => preloadRoute('/event-matches-page')}
                      onTouchStart={() => preloadRoute('/event-matches-page')}
                      onClick={handleCloseMenu}
                      className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors rounded-lg"
                    >
                      <Star size={18} className="text-yellow-500" />
                      <span className="font-medium text-sm">Your Events</span>
                    </Link>
                  </div>

                  {/* Admin Event Tools (Conditional) */}
                  {isAdmin && (
                    <div className="pt-2 pb-2 border-y border-neutral-100 dark:border-dark-200 my-2 space-y-1">
                      <p className="px-4 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-2">Admin Event Tools</p>

                      <Link
                        to="/admin/ai-event-matcher"
                        onClick={handleCloseMenu}
                        className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors rounded-lg"
                      >
                        <Sliders size={18} className="text-blue-500" />
                        <span className="font-medium text-sm">AI Event Matcher</span>
                      </Link>

                      <Link
                        to="/admin/event-interests"
                        onClick={handleCloseMenu}
                        className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors rounded-lg"
                      >
                        <Heart size={18} className="text-pink-500" />
                        <span className="font-medium text-sm">Event Interests</span>
                      </Link>

                      <Link
                        to="/admin/event-requirements"
                        onClick={handleCloseMenu}
                        className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors rounded-lg"
                      >
                        <Calendar size={18} className="text-indigo-500" />
                        <span className="font-medium text-sm">Admin Requirements</span>
                      </Link>

                      <Link
                        to="/admin/event-manager-media"
                        onClick={handleCloseMenu}
                        className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors rounded-lg"
                      >
                        <ImageIcon size={18} className="text-emerald-500" />
                        <span className="font-medium text-sm">Manager Media</span>
                      </Link>

                      <Link
                        to="/admin/event-manager-posts"
                        onClick={handleCloseMenu}
                        className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors rounded-lg"
                      >
                        <FileText size={18} className="text-amber-500" />
                        <span className="font-medium text-sm">Manager Posts</span>
                      </Link>
                    </div>
                  )}

                  {/* Events */}
                  <Link
                    to="/events"
                    onClick={handleCloseMenu}
                    className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-neutral-100 dark:hover:bg-dark-100 transition-colors rounded-lg active:bg-neutral-200 dark:active:bg-dark-200"
                  >
                    <PartyPopper size={18} className="text-amber-500" />
                    <span className="font-medium text-sm">Events</span>
                  </Link>

                  {/* Services */}
                  <Link
                    to="/services"
                    onClick={handleCloseMenu}
                    className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-neutral-100 dark:hover:bg-dark-100 transition-colors rounded-lg active:bg-neutral-200 dark:active:bg-dark-200"
                  >
                    <ShoppingBag size={18} className="text-blue-500" />
                    <span className="font-medium text-sm">Services</span>
                  </Link>

                  {/* Appearance / Theme Toggle */}
                  <button
                    onClick={toggleTheme}
                    className="w-full flex items-center justify-between px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-neutral-100 dark:hover:bg-dark-100 transition-colors rounded-lg active:bg-neutral-200 dark:active:bg-dark-200 text-left cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                        {resolvedTheme === 'dark' ? <Moon size={18} /> : <Sun size={18} />}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">Appearance</span>
                        <span className="text-[11px] text-neutral-500 dark:text-dark-400 capitalize">
                          {resolvedTheme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                        </span>
                      </div>
                    </div>

                    {/* Visual Toggle Pill */}
                    <div className={`w-10 h-5 flex items-center rounded-full p-0.5 transition-colors duration-300 ${resolvedTheme === 'dark' ? 'bg-purple-600 justify-end' : 'bg-neutral-300 dark:bg-neutral-700 justify-start'}`}>
                      <motion.div
                        layout
                        className="bg-white w-4 h-4 rounded-full shadow-md"
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    </div>
                  </button>

                  {/* Help Center */}
                  <Link
                    to="/help"
                    onClick={handleCloseMenu}
                    className="flex items-center gap-3 px-4 py-3 text-neutral-900 dark:text-dark-900 hover:bg-neutral-100 dark:hover:bg-dark-100 transition-colors rounded-lg active:bg-neutral-200 dark:active:bg-dark-200"
                  >
                    <HelpCircle size={18} className="text-neutral-500" />
                    <span className="font-medium text-sm">Help Center</span>
                  </Link>

                  {/* Logout */}
                  <button
                    onClick={() => {
                      handleCloseMenu();
                      setIsLogoutConfirmOpen(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-4 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors rounded-lg text-left"
                  >
                    <LogOut size={20} />
                    <span className="font-bold text-base">Sign Out</span>
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Logout Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
        onConfirm={async () => {
          setIsLogoutConfirmOpen(false);
          try {
            await auth.signOut();
            window.location.href = '/login';
          } catch (error) {
            console.error('Sign out failed:', error);
          }
        }}
        title="Sign Out"
        message="Are you sure you want to sign out of your account?"
        confirmText="Sign Out"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Mobile Search Overlay */}
      <MobileSearchOverlay isOpen={showSearch} onClose={() => setShowSearch(false)} />

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#080A0F]/95 backdrop-blur-md border-t border-neutral-200 dark:border-white/[0.08] pb-[env(safe-area-inset-bottom,0px)]">
        <div className="flex items-center justify-around h-16 px-2 max-w-lg mx-auto">
          {(() => {
            const items = navItems.filter((i) => i.label !== 'Collections' && i.label !== 'Messages' && i.label !== 'Events');
            const left = items.slice(0, Math.ceil(items.length / 2));
            const right = items.slice(Math.ceil(items.length / 2));
            return (
              <>
                {left.map((item) => {
                  const Icon = item.icon;
                  // Search button opens overlay instead of navigating
                  if (item.label === 'Explore') {
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          setShowMobileApexMenu(false);
                          setShowSearch(true);
                        }}
                        className={`flex items-center justify-center w-12 h-12 rounded-xl touch-manipulation transition-colors ${showSearch
                          ? 'bg-[#581C87] text-white shadow-md shadow-purple-950/60 border border-purple-500/40 font-semibold'
                          : 'text-slate-400 hover:text-white'
                          }`}
                        aria-label="Search"
                      >
                        <Icon size={22} />
                      </button>
                    );
                  }
                  const isActive = (() => {
                    const fullPath = location.pathname + (location.search || '');
                    if (item.path.includes('?')) return fullPath === item.path;
                    return location.pathname === item.path;
                  })();
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onPointerEnter={() => preloadRoute(item.path)}
                      onTouchStart={() => preloadRoute(item.path)}
                      onClick={() => {
                        setShowMobileApexMenu(false);
                        setShowSearch(false);
                      }}
                      className={`flex items-center justify-center w-12 h-12 rounded-xl touch-manipulation transition-colors ${isActive
                        ? 'bg-[#581C87] text-white shadow-md shadow-purple-950/60 border border-purple-500/40 font-semibold'
                        : 'text-slate-400 hover:text-white'
                        }`}
                    >
                      <Icon size={22} />
                    </Link>
                  );
                })}

                {/* Inline Create button (center) */}
                <Link
                  to="/create"
                  aria-label="Create"
                  className="group touch-manipulation"
                  onPointerEnter={() => preloadRoute('/create')}
                  onTouchStart={() => preloadRoute('/create')}
                  onClick={() => {
                    setShowMobileApexMenu(false);
                    setShowSearch(false);
                  }}
                >
                  <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }} className="flex items-center justify-center">
                    <div className="shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 via-pink-500 to-purple-600 p-[2px] shadow-md shadow-purple-500/20">
                      <div className="w-full h-full rounded-full flex items-center justify-center bg-[#121620]">
                        <Plus size={20} strokeWidth={2.5} className="text-purple-400" />
                      </div>
                    </div>
                  </motion.div>
                </Link>

                {right.map((item) => {
                  const Icon = item.icon;
                  const isActive = (() => {
                    const fullPath = location.pathname + (location.search || '');
                    if (item.path.includes('?')) return fullPath === item.path;
                    return location.pathname === item.path;
                  })();
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onPointerEnter={() => preloadRoute(item.path)}
                      onTouchStart={() => preloadRoute(item.path)}
                      onClick={() => {
                        setShowMobileApexMenu(false);
                        setShowSearch(false);
                      }}
                      className={`flex items-center justify-center w-12 h-12 rounded-xl touch-manipulation transition-colors ${isActive
                        ? 'bg-[#581C87] text-white shadow-md shadow-purple-950/60 border border-purple-500/40 font-semibold'
                        : 'text-slate-400 hover:text-white'
                        }`}
                    >
                      <Icon size={22} />
                    </Link>
                  );
                })}
              </>
            );
          })()}
        </div>
      </nav>
    </>
  );
};

export default MobileNav;
